import { useEffect, useRef, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useMutation, useQuery } from "@tanstack/react-query";
import BaseButton from "../../../components/ui/BaseButton";
import BaseDataTableV2 from "../../../components/ui/BaseDataTableV2";
import BaseDropdownV2 from "../../../components/ui/BaseDropdownV2";

import { useCurrentUser } from "../../../hooks/useCurrentUser";
import CompleteProfileModal from "../../WorkJobDetails/CompleteProfileModal";
import useAuth from "../../../hooks/useAuth";
import AddEditBankAccountModal from "./components/AddEditBankAccountModal";

import BaseConfirmModal from "../../../components/ui/BaseConfirmModal";
import { showToast } from "../../../store/toast/toastSlice";
import { useDispatch } from "react-redux";
import { deleteBankAccount, fetchBankAccounts } from "../../../services/api/bankAccount";
import { useParams } from "react-router-dom";
import { fetchUserDetails } from "../../../services/api/user";
import { debounce } from "lodash";
import BaseTextField from "../../../components/ui/BaseTextField";

const menuOptions = [
  {
    label: "Edit",
    value: "EDIT",
  },

  {
    label: "Delete",
    value: "DELETE",
  },
];

const BankAccountsTable = ({ userId: userIdProp = null }) => {
  const { id: routeUserId } = useParams();
  const userId = userIdProp ?? routeUserId;
  const [isAddEditBankAccountModalOpen, setIsAddEditBankAccountModalOpen] = useState(false);
  const dispatch = useDispatch();
  const [bankAccountsList, setBankAccountsList] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [isProfileCompleted, setIsProfileCompleted] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const { authRole } = useAuth();
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const baseQueryParams = {
    limit: 20,
    page: 1,
    search: "",
  };
  const [queryParams, setQueryParams] = useState(baseQueryParams);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [rowCount, setRowCount] = useState(0);

  const {
    isPending: isUserLoading,
    error: isUserError,
    data: userData,
    refetch: refetchUserDetails,
  } = useQuery({
    queryKey: ["userData", userId],
    queryFn: () => fetchUserDetails(userId),
    retry: 2,
    enabled: !!userId,
  });

  const {
    isPending: isDeleteBranchApiLoading,
    error: isDeleteBranchApiError,
    data: deleteBranchData,
    mutateAsync: deleteBankAccountMutation,
  } = useMutation({
    mutationFn: deleteBankAccount,
    enabled: false,
  });

  const {
    data: bankAccountsData,
    isPending: isFetchBankAccountsApiLoading,
    refetch,
  } = useQuery({
    queryKey: ["bankAccountsData", queryParams.limit, queryParams.page, queryParams.search],
    queryFn: () =>
      fetchBankAccounts({
        userId: userData?.id,
        page: queryParams.page,
        limit: queryParams.limit,
        search: queryParams.search,
      }),
    keepPreviousData: true, // <-- important
  });

  useEffect(() => {
    if (bankAccountsData?.data) {
      setBankAccountsList(bankAccountsData.data);
    }
  }, [bankAccountsData]);

  // Keep the footer count in step with the rows actually rendered - the list is
  // also updated locally after an add/edit/delete, before the refetch lands.
  useEffect(() => {
    setRowCount(bankAccountsList?.length || 0);
  }, [bankAccountsList]);

  const columns = [
    {
      field: "accountHolderName",
      headerName: "Account Holder Name",
      width: 250,
      renderCell: (params) => (
        <p
          className="text-primary cursor-pointer"
          onClick={() => {
            setSelectedBankAccount(params.row);
            setIsAddEditBankAccountModalOpen(true);
          }}
        >
          {params.value}
        </p>
      ),
    },
    {
      field: "bankName",
      headerName: "Bank Name",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "accountNumber",
      headerName: "Account Number",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "iban",
      headerName: "IBAN",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "bankAddress",
      headerName: "Bank Address",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "currency",
      headerName: "Currency",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "routingNumber",
      headerName: "Routing Number",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "accountType",
      headerName: "Account Type",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "sortCode",
      headerName: "Sort Code",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "bsb",
      headerName: "BSB",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "institutionNumber",
      headerName: "Institution Number",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "transitNumber",
      headerName: "Transit Code",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "bankCode",
      headerName: "Bank Code",
      width: 250,
      renderCell: (params) => <p>{params?.value}</p>,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 250,
      renderCell: (params) => {
        return (
          <BaseDropdownV2
            menuOptions={menuOptions}
            triggerElement={<i className="fa-solid fa-ellipsis cursor-pointer ml-4 text-base"></i>}
            onItemClick={(item) => handleItemClick(item, params?.row)}
          />
        );
      },
    },
  ];
  const updateQueryParamsDebounced = useRef(
    debounce((query) => {
      setQueryParams((prev) => ({
        ...prev,
        search: query.trim() || "",
        page: 1, // reset to first page when searching
      }));
    }, 500),
  ).current;

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    updateQueryParamsDebounced(query);
  };
  const handlePaginationChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);

    setQueryParams((prev) => ({
      ...prev,
      page: newPaginationModel.page + 1,
      limit: newPaginationModel.pageSize,
    }));
  };

  const handleItemClick = async (value, data) => {
    setSelectedBankAccount(data);
    if (value === "EDIT") {
      setIsAddEditBankAccountModalOpen(true);
      setIsEditMode(true);
    } else if (value === "DELETE") {
      setIsDeleteConfirmationModalOpen(true);
    }
  };

  const handleDeleteBankAccountButton = async () => {
    try {
      const deletedBankAccount = await deleteBankAccountMutation({
        id: selectedBankAccount?.id,
        userId: selectedBankAccount?.userId,
      });
      setBankAccountsList((prevAccount) =>
        prevAccount.filter((b) => b.id !== deletedBankAccount.data.id),
      );
      setIsDeleteConfirmationModalOpen(false);
      refetch();
      dispatch(
        showToast({
          message: "Bank Account deleted successfully",
          type: "success",
        }),
      );
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  return (
    <>
      {bankAccountsList?.length === 0 && (
        <BaseCard elevation={0} className="py-4 px-5 rounded-xl shadow-lg ">
          <div className="flex justify-between w-full">
            <div className="w-2/6"></div>

            <BaseButton
              color="primary"
              className="!py-[5px]"
              onClick={() => {
                /*                 if (userData?.status !== "APPROVED") {
                  setIsProfileCompleted(true);
                  return;
                } */
                setIsAddEditBankAccountModalOpen(true);
                setSelectedBankAccount(null);
                setIsEditMode(true);
              }}
            >
              Add New Bank Account
            </BaseButton>
          </div>
        </BaseCard>
      )}

      <BaseDataTableV2
        className="max-h-[calc(100vh-320px)] overflow-auto"
        columns={columns}
        data={bankAccountsList}
        isLoading={isFetchBankAccountsApiLoading || isDeleteBranchApiLoading}
        isCellEditable={(params) => false}
        hideFooter={false}
        pagination
        rowCount={rowCount}
        pageSizeOptions={[10, 20, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        paginationMode="server"
      />

      {isAddEditBankAccountModalOpen && (
        <AddEditBankAccountModal
          userData={userData}
          open={isAddEditBankAccountModalOpen}
          close={setIsAddEditBankAccountModalOpen}
          setBankAccountsList={setBankAccountsList}
          refetchBankAccounts={refetch}
          selectedBankAccount={selectedBankAccount}
          setSelectedBankAccount={setSelectedBankAccount}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
        />
      )}

      {isDeleteConfirmationModalOpen && (
        <BaseConfirmModal
          open={isDeleteConfirmationModalOpen}
          onClose={() => setIsDeleteConfirmationModalOpen(false)}
          cancelText="Cancel"
          confirmText="Delete"
          confirmButtonColor="error"
          title="Delete Bank Account"
          isLoading={isDeleteBranchApiLoading}
          description={
            <p>
              Are you sure you want to delete the Account
              {
                <span className="text-red-600 font-medium mx-1 capitalize">
                  "{selectedBankAccount?.accountHolderName}"
                </span>
              }
              ? This action cannot be undone.
            </p>
          }
          onConfirm={handleDeleteBankAccountButton}
        />
      )}

      {/* {isProfileCompleted && (
        <CompleteProfileModal
          isProfileCompleted={isProfileCompleted}
          setIsProfileCompleted={setIsProfileCompleted}
          status={userData?.status}
          authRole={authRole}
        />
      )} */}
    </>
  );
};

export default BankAccountsTable;
